// Stage 5 C-A — Client Portal router.
//
// Locked five-destination information architecture (§3):
//   Overview / Programme / Design / Files / Actions.
// Paid Services (§18–§23) are CONTEXTUAL detail routes reached from
// Overview / Actions / Files.  No permanent Paid Services /
// Commercial / Contracts / Payments nav entry.
// No standalone portal inbox (§26).  No 562-07 (§24).
// Audience-separated auth (§4): portal session ≠ admin access.
import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Route, Routes, useNavigate } from "react-router-dom";
import { portalApi } from "./portalApi";
import { Button, Pill } from "./PortalUi";
import PortalOverview from "./PortalOverview";
import PortalProgramme from "./PortalProgramme";
import { PortalDesign, PortalDesignIssue } from "./PortalDesign";
import { PortalFiles } from "./PortalFiles";
import { PortalFileCategoriesProvider } from "./context/PortalFileCategoriesContext";
import { PortalActions, PortalRfiDetail } from "./PortalActions";
import {
    PortalPackageDetail, PortalContractPosition, PortalContractChange,
    PortalCertificateDetail, PortalConstructionProgress,
} from "./PortalPaidServices";
import { usePageMeta } from "../../components/Layout";

// ---------- Portal login (§4/§61 — Portal ID + Password only) ----------

// Crockford-32 (no I, L, O, U) — matches the backend
// `_PUBLIC_ID_RE` enforced at `/api/portal/auth/login`.
const PORTAL_ID_RE = /^SG4-[0-9A-HJKMNP-TV-Z]{5}-[0-9A-HJKMNP-TV-Z]{5}$/;

export function PortalLogin() {
    usePageMeta({ title: "Sign in | Studio G4 Portal", robots: "noindex,nofollow" });
    const [portalId, setPortalId] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const idTrimmed = portalId.trim().toUpperCase();
    const idValid = PORTAL_ID_RE.test(idTrimmed);
    const canSubmit = idValid && password.length > 0 && !busy;

    function handleIdBlur() {
        // Normalise on blur so the submit gate reflects the server
        // expectation.  Also strips trailing whitespace clients
        // sometimes paste in from emails.
        setPortalId(portalId.trim().toUpperCase());
    }

    async function submit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true); setErr("");
        try {
            await portalApi.login(idTrimmed, password);
            navigate("/portal", { replace: true });
        } catch (ex) {
            const status = ex?.response?.status;
            const detail = ex?.response?.data?.detail;
            if (status === 429) {
                const retryAfter = parseInt(
                    ex.response.headers?.["retry-after"] || "0", 10);
                const mins = Math.max(1, Math.ceil(retryAfter / 60));
                setErr(`Too many failed attempts. Try again in ${mins} `
                        + `minute${mins === 1 ? "" : "s"}.`);
            } else if (status === 401
                        && typeof detail === "string"
                        && detail.includes("updated login credentials")) {
                // Pass legacy-UUID hint through verbatim so the user
                // sees the explicit format correction.
                setErr(detail);
            } else if (status === 401) {
                // Generic shape — never echo the server message
                // (enumeration safety).
                setErr("Invalid Portal ID or password.");
            } else if (status && status >= 500) {
                setErr("Something went wrong. Please try again.");
            } else {
                setErr("Invalid Portal ID or password.");
            }
        } finally { setBusy(false); }
    }

    return (
        <div className="min-h-screen bg-[#F5F3EE] flex items-center justify-center p-4" data-testid="portal-login">
            <form onSubmit={submit} className="w-full max-w-md bg-white rounded-lg border border-[#E5E2DC] p-8 shadow-sm">
                <div className="mb-6">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#A8543F] font-manrope font-semibold">Studio G4</div>
                    <h1 className="mt-1 font-manrope text-[26px] font-semibold text-charcoal">Client portal</h1>
                    <p className="mt-1 text-[12.5px] text-[#6B6B67]">Sign in with your Portal ID and password.</p>
                </div>
                {err && (
                    <div className="mb-4 text-[12.5px] text-[#8C2E1E] bg-[#F6E0DA] border border-[#E9C6BE] rounded p-2.5"
                         data-testid="portal-login-err">
                        {err}
                    </div>
                )}
                <label className="block mb-4">
                    <span className="text-[11px] font-manrope font-semibold uppercase tracking-[0.1em] text-[#6B6B67]">Portal ID</span>
                    <input data-testid="portal-login-project-id"
                        className="mt-1 w-full px-3 py-2 border border-[#D7D4CC] rounded text-[14px] font-mono uppercase focus:outline-none focus:border-[#A8543F]"
                        type="text" value={portalId}
                        onChange={e => setPortalId(e.target.value)}
                        onBlur={handleIdBlur}
                        placeholder="SG4-ABCDE-FGHIJ"
                        autoComplete="off" required />
                    {portalId.length > 0 && !idValid && (
                        <span className="mt-1 block text-[11.5px] text-[#8C2E1E]"
                              data-testid="portal-login-id-hint">
                            Portal ID must follow the format
                            SG4-XXXXX-XXXXX.
                        </span>
                    )}
                </label>
                <label className="block mb-6">
                    <span className="text-[11px] font-manrope font-semibold uppercase tracking-[0.1em] text-[#6B6B67]">Password</span>
                    <input data-testid="portal-login-password"
                        className="mt-1 w-full px-3 py-2 border border-[#D7D4CC] rounded text-[14px] focus:outline-none focus:border-[#A8543F]"
                        type="password" value={password} onChange={e => setPassword(e.target.value)}
                        autoComplete="current-password" required />
                </label>
                <Button type="submit" variant="primary" disabled={!canSubmit} testid="portal-login-submit" className="w-full">
                    {busy ? "Signing in…" : "Log in"}
                </Button>
                <p className="mt-6 text-[11.5px] text-[#8A8A84] leading-relaxed">
                    Access to this portal is provided by Studio G4. If you're unsure of your Portal ID, please contact your project team.
                </p>
            </form>
        </div>
    );
}

// ---------- Protected shell (§3 — five canonical destinations) ----------

const NAV = [
    { to: "/portal", label: "Overview", end: true, id: "overview",
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5"/></svg>) },
    { to: "/portal/programme", label: "Programme", id: "programme",
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>) },
    { to: "/portal/design", label: "Design", id: "design",
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z"/></svg>) },
    { to: "/portal/files", label: "Files", id: "files",
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z"/></svg>) },
    { to: "/portal/actions", label: "Actions", id: "actions",
      icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>) },
];

export function PortalLayout() {
    const [me, setMe] = useState(null);
    const [project, setProject] = useState(null);
    const [ready, setReady] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    useEffect(() => {
        portalApi.me().then(r => { setMe(r.data); setReady(true); })
            .catch(() => navigate("/portal/login", { replace: true }));
    }, [navigate]);
    useEffect(() => {
        if (!me) return;
        portalApi.overview().then(r => setProject(r.data.project)).catch(() => {});
    }, [me]);
    async function logout() {
        try { await portalApi.logout(); } catch {}
        navigate("/portal/login", { replace: true });
    }

    if (!ready) {
        return <div className="min-h-screen bg-[#F5F3EE] flex items-center justify-center text-[13px] text-[#6B6B67]" data-testid="portal-loading">Loading…</div>;
    }
    const SIDEBAR = (
        <aside className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static inset-y-0 left-0 z-30 w-[268px] h-screen overflow-y-auto bg-[#1D1D1B] text-[#F5F3EE] flex flex-col transition-transform`} data-testid="portal-sidebar">
            <div className="px-6 pt-6 pb-3">
                <img src="/SG4_Logo_Light.png" alt="Studio G4"
                     className="w-full max-w-[90px] h-auto select-none" data-testid="portal-sidebar-logo" />
            </div>
            <div className="px-6 mb-3">
                <span className="inline-block rounded-md bg-[#A8543F]/50 text-white font-manrope font-semibold text-[10px] px-1.5 py-[3px]" data-testid="portal-sidebar-tag">Client Portal</span>
            </div>
            <nav className="mt-2 flex-1 px-3">
                {NAV.map(n => (
                    <NavLink key={n.id} to={n.to} end={n.end}
                        data-testid={`portal-nav-${n.id}`}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                            "relative flex items-center gap-3 rounded-md px-4 py-2.5 my-0.5 font-manrope text-[13.5px] transition-colors " +
                            (isActive
                                ? "bg-[#2A2926] text-white font-semibold"
                                : "text-[#C6C2B8] hover:bg-[#242320] hover:text-white")
                        }>
                        {({ isActive }) => (<>
                            {isActive && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-[#A8543F]" />}
                            <span className={isActive ? "text-[#A8543F]" : "text-[#8E8A80]"}>{n.icon}</span>
                            <span>{n.label}</span>
                        </>)}
                    </NavLink>
                ))}
            </nav>
            {project && (
                <div className="mx-3 mb-3 rounded-lg border border-white/10 bg-white/[0.04] p-4" data-testid="portal-sidebar-project-card">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#8E8A80] font-manrope font-semibold">Project:</div>
                    <div className="mt-1 text-[13px] text-white font-manrope font-semibold" data-testid="portal-sidebar-client-name">{project.client_name || me?.client_name}</div>
                    {project.project_title && (
                        <div className="mt-1 text-[12.5px] text-[#D8D4CB]" data-testid="portal-sidebar-project-title">{project.project_title}</div>
                    )}
                    {project.postcode && (
                        <div className="mt-1 text-[12.5px] text-[#D8D4CB]" data-testid="portal-sidebar-postcode">{project.postcode}</div>
                    )}
                    <div className="mt-3 text-[10px] uppercase tracking-[0.16em] text-[#8E8A80] font-manrope font-semibold">Project ID:</div>
                    <div className="mt-1 text-[12.5px] text-white font-mono" data-testid="portal-sidebar-project-ref">{project.project_ref}</div>
                </div>
            )}
            <div className="px-3 pb-4">
                <button data-testid="portal-logout" onClick={logout}
                    className="w-full flex items-center gap-3 rounded-md px-4 py-2.5 font-manrope text-[13.5px] text-[#C6C2B8] hover:bg-[#242320] hover:text-white transition-colors">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><path d="M10 17l5-5-5-5M15 12H3"/></svg>
                    <span>Log out</span>
                </button>
            </div>
        </aside>
    );
    return (
        <div className="h-screen bg-[#F5F3EE] flex overflow-hidden" data-testid="portal-shell">
            <button
                className="md:hidden fixed top-3 left-3 z-40 bg-[#1D1D1B] text-white rounded px-3 py-1.5 text-[13px] font-manrope font-semibold"
                onClick={() => setMobileOpen(o => !o)}
                data-testid="portal-mobile-toggle"
                aria-label="Toggle navigation"
            >
                {mobileOpen ? "Close" : "Menu"}
            </button>
            {SIDEBAR}
            {mobileOpen && (
                <div className="md:hidden fixed inset-0 bg-black/40 z-20" onClick={() => setMobileOpen(false)} />
            )}
            <main className="flex-1 min-w-0 h-screen overflow-y-auto px-6 lg:px-12" data-testid="portal-main">
                <Outlet />
            </main>
        </div>
    );
}

// ---------- Router mount ----------

export default function PortalRoutes() {
    usePageMeta({ robots: "noindex,nofollow" });
    return (
        <Routes>
            <Route path="login" element={<PortalLogin />} />
            <Route path="" element={
                <PortalFileCategoriesProvider>
                    <PortalLayout />
                </PortalFileCategoriesProvider>
            }>
                <Route index element={<PortalOverview />} />
                <Route path="programme" element={<PortalProgramme />} />
                <Route path="design" element={<PortalDesign />} />
                <Route path="design/issues/:issueId" element={<PortalDesignIssue />} />
                <Route path="files" element={<PortalFiles />} />
                <Route path="files/:fileId" element={<PortalFiles />} />
                <Route path="files/:fileId/preview" element={<PortalFiles />} />
                <Route path="files/:fileId/versions" element={<PortalFiles />} />
                <Route path="actions" element={<PortalActions />} />
                <Route path="rfis/:rfiId" element={<PortalRfiDetail />} />
                {/* Paid Services — contextual overlays, NOT a permanent
                    top-level nav entry (§3 / §18). */}
                <Route path="paid-services/packages/:packageId" element={<PortalPackageDetail />} />
                <Route path="paid-services/contracts/:contractId" element={<PortalContractPosition />} />
                <Route path="paid-services/contract-changes/:changeId" element={<PortalContractChange />} />
                <Route path="paid-services/certificates/:certId" element={<PortalCertificateDetail />} />
                <Route path="paid-services/construction-progress" element={<PortalConstructionProgress />} />
            </Route>
        </Routes>
    );
}
